import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import OnePersonIcon from '../../_icons/OnePersonIcon';
import WhiteOnePersonIcon from '../../_icons/WhiteOnePersonIcon';
import TimeIcon from '../../_icons/TimeIcon';
import GolfSkeleton from './golfSkeleton/golfSkeleton';
import { getSpotWaitingInfoAll } from '../../api/golf';
import WhiteTimeIcon from '../../_icons/WhiteTimeIcon';

// 스팟 상태별 스타일 정의
const STATUS_STYLES = {
  RUNNING_IN_USE: {
    label: 'is using',
    statusColor: '#0F7F85',
    backgroundColor: '#FFFFFF',
    textColor: '#0F7F85',
    badgeBackground: '#FFFFFF',
  },
  READY_RESERVED: {
    label: '사용예정',
    statusColor: '#FFFFFF',
    backgroundColor: '#4AB3BC',
    textColor: '#FFFFFF',
    badgeBackground: '#4AB3BC',
  },
  READY: {
    label: 'Available for reservation',
    statusColor: '#FFFFFF',
    backgroundColor: '#0F7F85',
    textColor: '#FFFFFF',
    badgeBackground: 'transparent',
  },
  SUSPENDED: {
    label: 'Unavailable for reservation',
    statusColor: '#FFFFFF',
    backgroundColor: '#A6A6A6',
    textColor: '#FFFFFF',
    badgeBackground: '#A6A6A6',
  },
  DISABLED: {
    label: 'Unavailable for reservation',
    statusColor: '#FFFFFF',
    backgroundColor: '#A6A6A6',
    textColor: '#FFFFFF',
    badgeBackground: 'transparent',
  },
  DEFAULT: {
    label: '',
    statusColor: '#A6A6A6',
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    badgeBackground: 'transparent',
  },
};

const GolfSpotWidget = ({ spot, onClick, isMoveMode = false, isDisabled = false, isLoading = false }) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  // 기본 데이터 추출
  const currentStatus = spot?.spotStatus;
  const reservation = spot?.reservation;

  // 조건 미리 계산
  // 스팟 이동 기능: reservation이 있고 (RUNNING 또는 RESERVED) 상태이면 예약 정보 표시
  const hasReservation = reservation && (reservation.status === 'RUNNING' || reservation.status === 'RESERVED');
  const isRunningInUse = hasReservation;
  const showEndTime = hasReservation && reservation?.endTime;

  // 대기자만 있을 때 (현재 이용중인 사람이 없고 대기자만 있는 경우)
  const hasOnlyWaitings = !hasReservation && spot?.waitings >= 1;

  // 대기자 목록 조회 (대기자만 있을 때만)
  const areaId = spot?.areaId || spot?.golfAreaId;
  const { data: waitingData } = useQuery({
    queryKey: ['spotWaitingList', user?.token, areaId, spot?.id],
    queryFn: () => getSpotWaitingInfoAll(user.token, areaId),
    enabled: !!hasOnlyWaitings && !!user?.token && !!areaId && !!spot?.id,
    refetchInterval: 3000, // 3초마다 자동으로 데이터 갱신
    refetchIntervalInBackground: true,
    select: data => {
      if (!data || !Array.isArray(data.spots)) {
        return { waitingList: [] };
      }
      const matchedSpot = data.spots.find(s => s.spotId === spot.id);
      if (!matchedSpot || !Array.isArray(matchedSpot.waitingList)) {
        return { waitingList: [] };
      }
      return { waitingList: matchedSpot.waitingList || [] };
    },
  });

  const waitingList = useMemo(() => waitingData?.waitingList || [], [waitingData?.waitingList]);

  // 현재 상태에 맞는 스타일
  // 대기자만 있을 때 (waitings가 있고 reservation이 null일 때) READY_RESERVED 스타일 사용
  const style = isRunningInUse
    ? STATUS_STYLES.RUNNING_IN_USE
    : hasOnlyWaitings
      ? STATUS_STYLES.READY_RESERVED
      : STATUS_STYLES[currentStatus] || STATUS_STYLES.DEFAULT;

  // 시간 포맷팅 함수
  const formatEndTime = endTime => {
    if (!endTime) return '';
    try {
      const date = new Date(endTime);
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    } catch {
      return endTime;
    }
  };

  const calculateRemainingMinutes = endTime => {
    if (!endTime) return null;
    try {
      const diffMs = new Date(endTime) - new Date();
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes > 0 ? diffMinutes : 0;
    } catch {
      return null;
    }
  };

  // 실시간으로 남은 시간 업데이트
  const [remainingMinutes, setRemainingMinutes] = useState(() => calculateRemainingMinutes(reservation?.endTime));

  useEffect(() => {
    if (!showEndTime || !reservation?.endTime) {
      setRemainingMinutes(null);
      return;
    }

    // 즉시 계산
    const updateRemainingTime = () => {
      const minutes = calculateRemainingMinutes(reservation?.endTime);
      setRemainingMinutes(minutes);
    };

    // 초기 계산
    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 3000); // 10초마다 업데이트

    return () => clearInterval(interval);
  }, [showEndTime, reservation?.endTime]);

  // 대기자만 있을 때 시간 계산 (대기자가 시작할 때까지의 남은 시간)
  const calculateWaitingTime = useCallback(() => {
    if (!hasOnlyWaitings || waitingList.length === 0) return null;

    const firstWaiting = waitingList[0];
    if (!firstWaiting?.startTime) return null;

    try {
      const startTime = new Date(firstWaiting.startTime);
      const now = new Date();
      const diffMs = startTime - now;

      // 대기자가 시작할 때까지의 남은 시간 (분)
      const remainingMinutesToStart = Math.max(0, Math.floor(diffMs / (1000 * 60)));

      return remainingMinutesToStart;
    } catch {
      return null;
    }
  }, [hasOnlyWaitings, waitingList]);

  const [waitingTotalMinutes, setWaitingTotalMinutes] = useState(() => calculateWaitingTime());

  useEffect(() => {
    if (!hasOnlyWaitings || waitingList.length === 0) {
      setWaitingTotalMinutes(null);
      return;
    }

    const updateWaitingTime = () => {
      const minutes = calculateWaitingTime();
      setWaitingTotalMinutes(minutes);
    };

    updateWaitingTime();
    const interval = setInterval(updateWaitingTime, 10000); // 10초마다 업데이트

    return () => clearInterval(interval);
  }, [hasOnlyWaitings, waitingList, calculateWaitingTime]);

  // 사용종료시간 및 남은시간 섹션
  const renderEndTimeInfo = () => {
    if (showEndTime) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
            <TimeIcon />
            <Typography sx={{ fontSize: '12px', fontWeight: '500', color: '#000' }}>{t('Usage end time')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>
              {formatEndTime(reservation?.endTime)}
            </Typography>
            {remainingMinutes !== null && remainingMinutes > 0 && (
              <Typography sx={{ fontSize: '14px', fontWeight: '600', color: '#000' }}>
                ({remainingMinutes}
                {t('minutes remaining')})
              </Typography>
            )}
          </Box>
        </Box>
      );
    }

    // 대기자만 있을 때 시간 표시
    if (hasOnlyWaitings && waitingTotalMinutes !== null) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
            <WhiteTimeIcon />
            <Typography sx={{ fontSize: '12px', fontWeight: '500', color: '#FFFFFF' }}>
              {t('Expected usage time')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#fff' }}>
              {formatEndTime(waitingList[0]?.endTime)}
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>
              ({waitingTotalMinutes}
              {t('minutes remaining')})
            </Typography>
          </Box>
        </Box>
      );
    }

    return null;
  };

  const isClickDisabled = spot.spotStatus === 'SUSPENDED' || spot.spotStatus === 'DISABLED' || isDisabled;
  const shouldDim = isMoveMode && spot.spotStatus !== 'READY';

  // 로딩 중일 때 스켈레톤 UI 표시
  if (isLoading) {
    return <GolfSkeleton />;
  }

  return (
    <Box
      onClick={isClickDisabled ? undefined : onClick}
      sx={{
        width: '160px',
        cursor: isClickDisabled ? 'not-allowed' : 'pointer',
        height: '164px',
        backgroundColor: shouldDim ? style.backgroundColor : style.backgroundColor,
        borderRadius: '8px',
        boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.16)',
        p: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        opacity: shouldDim ? 0.5 : 1,
        position: 'relative',
        zIndex: isMoveMode && !shouldDim ? 1002 : 'auto',
        border: isClickDisabled ? 'none' : '1px solid #FFFFFF',
        '&:hover': {
          boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.2)',
        },
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '20px', fontWeight: '600', color: style.textColor }}>{spot?.spotName}</Typography>
        {isRunningInUse && (
          <Typography sx={{ fontSize: '16px', fontWeight: '500', color: style.textColor }}>
            {(reservation?.memberName?.length > 4
              ? reservation.memberName.slice(0, 4) + '..'
              : reservation?.memberName) || '게스트'}
          </Typography>
        )}
        {hasOnlyWaitings && (
          <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#FFFFFF' }}>
            {waitingList[0]?.memberName || '게스트'}
          </Typography>
        )}
      </Box>
      {renderEndTimeInfo()}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 'auto' }}>
        {spot.waitings >= 1 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {hasOnlyWaitings ? <WhiteOnePersonIcon /> : <OnePersonIcon />}
            <Typography sx={{ fontSize: '20px', fontWeight: '600', color: hasOnlyWaitings ? '#FFFFFF' : '#0F7F85' }}>
              {spot.waitings}
            </Typography>
          </Box>
        ) : null}
        <Box
          sx={{
            border: `1px solid ${style.statusColor}`,
            borderRadius: '999px',
            px: '12px',
            py: '4px',
            ml: 'auto',
            backgroundColor: style.badgeBackground,
          }}>
          <Typography sx={{ fontSize: '12px', fontWeight: '500', color: style.statusColor }}>
            {t(style.label)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GolfSpotWidget;
